import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Save, Edit, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { beforeAfterPortfolio as initialPortfolio } from '../mock';
import BeforeAfterSlider from '../components/BeforeAfterSlider';
import AdminPricing from '../components/AdminPricing';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('portfolio');
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    description: '',
    beforeImage: '',
    afterImage: '',
    results: ''
  });
  const [beforeImageFile, setBeforeImageFile] = useState(null);
  const [afterImageFile, setAfterImageFile] = useState(null);
  const [beforePreview, setBeforePreview] = useState('');
  const [afterPreview, setAfterPreview] = useState('');

  useEffect(() => {
    const isAdmin = localStorage.getItem('isAdmin');
    if (!isAdmin) {
      navigate('/admin');
      return;
    }

    fetchPortfolio();
  }, [navigate]);

  const fetchPortfolio = async () => {
    try {
      const response = await axios.get(`${API}/portfolio`);
      if (response.data && response.data.length > 0) {
        setPortfolioItems(response.data);
      } else {
        // Initialize with default data if empty
        await initializeDefaultData();
      }
    } catch (error) {
      console.error('Error fetching portfolio:', error);
      toast({ title: "Error", description: "Failed to load portfolio items" });
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const initializeDefaultData = async () => {
    try {
      for (const item of initialPortfolio) {
        await axios.post(`${API}/portfolio`, item, { headers: getAuthHeaders() });
      }
      fetchPortfolio();
    } catch (error) {
      console.error('Error initializing data:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('adminToken');
    navigate('/home');
    toast({
      title: "Logged Out",
      description: "Successfully logged out"
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    try {
      const finalData = { ...formData };
      
      // Convert uploaded files to base64
      if (beforeImageFile) {
        finalData.beforeImage = await convertToBase64(beforeImageFile);
      }
      if (afterImageFile) {
        finalData.afterImage = await convertToBase64(afterImageFile);
      }
      
      if (isEditing && currentItem) {
        await axios.put(`${API}/portfolio/${currentItem.id}`, finalData, { headers: getAuthHeaders() });
        toast({ title: "Updated", description: "Item updated successfully" });
      } else {
        await axios.post(`${API}/portfolio`, finalData, { headers: getAuthHeaders() });
        toast({ title: "Added", description: "Item added successfully" });
      }

      setIsEditing(false);
      setCurrentItem(null);
      setFormData({ title: '', category: '', description: '', beforeImage: '', afterImage: '', results: '' });
      setBeforeImageFile(null);
      setAfterImageFile(null);
      setBeforePreview('');
      setAfterPreview('');
      
      fetchPortfolio();
    } catch (error) {
      console.error('Error saving:', error);
      toast({ title: "Error", description: "Failed to save item" });
    }
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleBeforeImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBeforeImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setBeforePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAfterImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAfterImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAfterPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEdit = (item) => {
    setIsEditing(true);
    setCurrentItem(item);
    setFormData({
      title: item.title,
      category: item.category,
      description: item.description || '',
      beforeImage: item.beforeImage,
      afterImage: item.afterImage,
      results: item.results || ''
    });
    setBeforePreview(item.beforeImage);
    setAfterPreview(item.afterImage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this item?')) {
      try {
        await axios.delete(`${API}/portfolio/${id}`, { headers: getAuthHeaders() });
        toast({ title: "Deleted", description: "Item deleted successfully" });
        fetchPortfolio();
      } catch (error) {
        console.error('Error deleting:', error);
        toast({ title: "Error", description: "Failed to delete item" });
      }
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setCurrentItem(null);
    setFormData({ title: '', category: '', description: '', beforeImage: '', afterImage: '', results: '' });
    setBeforeImageFile(null);
    setAfterImageFile(null);
    setBeforePreview('');
    setAfterPreview('');
  };

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <header className="bg-white border-b border-black/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-light" style={{ fontFamily: 'Georgia, serif' }}>Admin Dashboard</h1>
            <p className="text-sm text-black/50 font-light mt-1">Manage your website content</p>
          </div>
          <Button onClick={handleLogout} variant="outline">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-8">
            <button
              onClick={() => setActiveTab('portfolio')}
              className={`py-4 px-2 font-medium border-b-2 transition-colors ${
                activeTab === 'portfolio'
                  ? 'border-black text-black'
                  : 'border-transparent text-black/50 hover:text-black'
              }`}
            >
              Portfolio Management
            </button>
            <button
              onClick={() => setActiveTab('pricing')}
              className={`py-4 px-2 font-medium border-b-2 transition-colors ${
                activeTab === 'pricing'
                  ? 'border-black text-black'
                  : 'border-transparent text-black/50 hover:text-black'
              }`}
            >
              Pricing Management
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {activeTab === 'portfolio' ? (
          <>
            <div className="bg-white border border-black/10 p-8 mb-12">
              <h2 className="text-2xl font-light mb-6" style={{ fontFamily: 'Georgia, serif' }}>
                {isEditing ? 'Edit Item' : 'Add New Item'}
              </h2>
          
          <form onSubmit={handleSave} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-light mb-2">Title</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  placeholder="Project Title"
                />
              </div>
              <div>
                <label className="block text-sm font-light mb-2">Category</label>
                <Input
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                  placeholder="E.g., Web Design"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-light mb-2">Description</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description..."
                rows={3}
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-light mb-2">Before Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleBeforeImageChange}
                  className="w-full px-4 py-3 border border-black/20 focus:border-black transition-colors cursor-pointer"
                />
                {beforePreview && (
                  <div className="mt-4 border border-black/10" style={{ paddingBottom: '75%', position: 'relative' }}>
                    <img src={beforePreview} alt="Before preview" className="absolute inset-0 w-full h-full object-cover" />
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-light mb-2">After Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAfterImageChange}
                  className="w-full px-4 py-3 border border-black/20 focus:border-black transition-colors cursor-pointer"
                />
                {afterPreview && (
                  <div className="mt-4 border border-black/10" style={{ paddingBottom: '75%', position: 'relative' }}>
                    <img src={afterPreview} alt="After preview" className="absolute inset-0 w-full h-full object-cover" />
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-light mb-2">Results</label>
              <Input
                value={formData.results}
                onChange={(e) => setFormData({ ...formData, results: e.target.value })}
                placeholder="+340% conversion"
              />
            </div>

            <div className="flex gap-4">
              <Button type="submit" className="bg-black hover:bg-black/90 text-white">
                <Save className="w-4 h-4 mr-2" />
                {isEditing ? 'Update' : 'Add'}
              </Button>
              {isEditing && (
                <Button type="button" onClick={handleCancel} variant="outline">
                  Cancel
                </Button>
              )}
            </div>
          </form>
        </div>

        <div>
          <h2 className="text-2xl font-light mb-8" style={{ fontFamily: 'Georgia, serif' }}>
            Items ({portfolioItems.length})
          </h2>

          <div className="space-y-8">
            {portfolioItems.map((item) => (
              <div key={item.id} className="bg-white border border-black/10 p-6">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <BeforeAfterSlider
                      beforeImage={item.beforeImage}
                      afterImage={item.afterImage}
                      title={item.title}
                    />
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h3 className="text-2xl font-light mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                        {item.title}
                      </h3>
                      <p className="text-xs uppercase text-black/50 mb-4">{item.category}</p>
                      {item.description && <p className="text-sm text-black/70 mb-4">{item.description}</p>}
                      {item.results && (
                        <div className="inline-block px-4 py-2 bg-black text-white text-sm">
                          {item.results}
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2 pt-4">
                      <Button onClick={() => handleEdit(item)} size="sm" variant="outline" className="flex-1">
                        <Edit className="w-3 h-3 mr-2" />
                        Edit
                      </Button>
                      <Button onClick={() => handleDelete(item.id)} size="sm" variant="outline" className="text-red-600">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
          </>
        ) : (
          <AdminPricing />
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
